/**
 * AppStore - Centralized State Management
 *
 * Single source of truth for cart, wishlist, and user state.
 * Uses Observable pattern to notify components of state changes.
 *
 * Features:
 * - Cart state synchronization across all components
 * - Wishlist persistence with localStorage
 * - Reactive UI updates via subscribe/notify
 * - State debugging tools
 * - Event emission for lifecycle hooks
 *
 * Usage:
 * // Subscribe to state changes
 * const unsubscribe = window.appStore.subscribe((state) => {
 *     window.debugLogger.log('Cart updated:', state.cart);
 * });
 *
 * // Update cart state
 * window.appStore.updateCart(cartData);
 *
 * // Add to wishlist (auto-saves to localStorage)
 * window.appStore.addToWishlist(productId);
 */

class AppStore {
    constructor() {
        // Application state
        this.state = {
            cart: null,              // Cart data from Shopify
            wishlist: [],            // Product IDs in wishlist
            user: null,              // User data (if logged in)
            loading: false,          // Global loading state
            error: null              // Global error state
        };

        // Subscribers (components listening to state changes)
        this.subscribers = [];

        // Debug mode (enabled in development)
        this.debug = window.location.hostname === 'localhost' ||
                     window.location.hostname.includes('127.0.0.1') ||
                     window.location.search.includes('debug=true');

        // Initialize store
        this.init();
    }

    /**
     * Initialize store - Load persisted data
     */
    init() {
        // Load wishlist from localStorage
        this.state.wishlist = this.loadWishlist();

        // Log initialization
        this.log('🏪 AppStore initialized', this.state);

        // Dispatch initialization event
        this.dispatchEvent('store:initialized', { state: this.state });
    }

    /**
     * Get current state (returns copy to prevent mutations)
     */
    getState() {
        return { ...this.state };
    }

    /**
     * Get specific state slice
     */
    getCart() {
        return this.state.cart;
    }

    getWishlist() {
        return [...this.state.wishlist];
    }

    getUser() {
        return this.state.user;
    }

    /**
     * Update state and notify subscribers
     * @param {Object} updates - Partial state updates
     */
    setState(updates) {
        const previousState = { ...this.state };

        // Merge updates into state
        this.state = {
            ...this.state,
            ...updates
        };

        // Log state change
        this.log('📊 State updated', {
            previous: previousState,
            current: this.state,
            changes: updates
        });

        // Notify all subscribers
        this.notify(this.state, previousState);

        // Dispatch state change event
        this.dispatchEvent('store:updated', {
            state: this.state,
            previousState,
            changes: updates
        });
    }

    /**
     * Subscribe to state changes
     * @param {Function} listener - Callback function
     * @returns {Function} Unsubscribe function
     */
    subscribe(listener) {
        if (typeof listener !== 'function') {
            window.debugLogger.error('AppStore: Listener must be a function');
            return () => {};
        }

        // Add listener to subscribers
        this.subscribers.push(listener);

        this.log(`👂 New subscriber added (total: ${this.subscribers.length})`);

        // Call listener immediately with current state
        listener(this.state);

        // Return unsubscribe function
        return () => {
            this.subscribers = this.subscribers.filter(l => l !== listener);
            this.log(`👋 Subscriber removed (remaining: ${this.subscribers.length})`);
        };
    }

    /**
     * Notify all subscribers of state change
     */
    notify(currentState, previousState) {
        this.subscribers.forEach(listener => {
            try {
                listener(currentState, previousState);
            } catch (error) {
                window.debugLogger.error('AppStore: Error in subscriber callback', error);
            }
        });
    }

    /**
     * ========================================
     * CART STATE MANAGEMENT
     * ========================================
     */

    /**
     * Update cart state
     * @param {Object} cart - Cart data from Shopify
     */
    updateCart(cart) {
        if (!cart) {
            window.debugLogger.warn('AppStore: Attempted to update cart with null/undefined');
            return;
        }

        this.setState({ cart });

        this.log('🛒 Cart updated', {
            itemCount: cart.item_count,
            total: cart.total_price
        });
    }

    /**
     * Clear cart state
     */
    clearCart() {
        this.setState({ cart: null });
        this.log('🛒 Cart cleared');
    }

    /**
     * Get cart item count
     */
    getCartItemCount() {
        return this.state.cart?.item_count || 0;
    }

    /**
     * Get cart total price (in cents)
     */
    getCartTotal() {
        return this.state.cart?.total_price || 0;
    }

    /**
     * ========================================
     * WISHLIST STATE MANAGEMENT
     * ========================================
     */

    /**
     * Load wishlist from localStorage
     */
    loadWishlist() {
        try {
            const stored = localStorage.getItem('zanova_wishlist');
            if (!stored) return [];

            const wishlist = JSON.parse(stored);

            // Validate it's an array
            if (!Array.isArray(wishlist)) {
                window.debugLogger.warn('AppStore: Invalid wishlist format in localStorage');
                return [];
            }

            this.log('💾 Wishlist loaded from localStorage', wishlist);
            return wishlist;

        } catch (error) {
            window.debugLogger.error('AppStore: Error loading wishlist', error);
            return [];
        }
    }

    /**
     * Save wishlist to localStorage
     */
    saveWishlist(wishlist) {
        try {
            localStorage.setItem('zanova_wishlist', JSON.stringify(wishlist));
            this.log('💾 Wishlist saved to localStorage', wishlist);

            // Dispatch wishlist saved event
            this.dispatchEvent('wishlist:saved', { wishlist });

        } catch (error) {
            window.debugLogger.error('AppStore: Error saving wishlist', error);

            // Dispatch error event
            this.dispatchEvent('wishlist:error', {
                error: 'Failed to save wishlist',
                details: error
            });
        }
    }

    /**
     * Add product to wishlist
     * @param {string|number} productId - Shopify product ID
     */
    addToWishlist(productId) {
        // Convert to string for consistency
        const id = String(productId);

        // Check if already in wishlist
        if (this.state.wishlist.includes(id)) {
            this.log('💝 Product already in wishlist', id);
            return;
        }

        // Add to wishlist
        const wishlist = [...this.state.wishlist, id];

        // Update state
        this.setState({ wishlist });

        // Save to localStorage
        this.saveWishlist(wishlist);

        this.log('💝 Added to wishlist', id);

        // Dispatch event
        this.dispatchEvent('wishlist:added', { productId: id, wishlist });
    }

    /**
     * Remove product from wishlist
     * @param {string|number} productId - Shopify product ID
     */
    removeFromWishlist(productId) {
        // Convert to string for consistency
        const id = String(productId);

        // Filter out the product
        const wishlist = this.state.wishlist.filter(wId => wId !== id);

        // Update state
        this.setState({ wishlist });

        // Save to localStorage
        this.saveWishlist(wishlist);

        this.log('💔 Removed from wishlist', id);

        // Dispatch event
        this.dispatchEvent('wishlist:removed', { productId: id, wishlist });
    }

    /**
     * Check if product is in wishlist
     * @param {string|number} productId - Shopify product ID
     * @returns {boolean}
     */
    isInWishlist(productId) {
        const id = String(productId);
        return this.state.wishlist.includes(id);
    }

    /**
     * Clear entire wishlist
     */
    clearWishlist() {
        this.setState({ wishlist: [] });
        this.saveWishlist([]);
        this.log('💔 Wishlist cleared');

        // Dispatch event
        this.dispatchEvent('wishlist:cleared');
    }

    /**
     * Get wishlist count
     */
    getWishlistCount() {
        return this.state.wishlist.length;
    }

    /**
     * ========================================
     * USER STATE MANAGEMENT
     * ========================================
     */

    /**
     * Update user state
     */
    updateUser(user) {
        this.setState({ user });
        this.log('👤 User updated', user);
    }

    /**
     * Clear user state (logout)
     */
    clearUser() {
        this.setState({ user: null });
        this.log('👤 User cleared');
    }

    /**
     * ========================================
     * LOADING & ERROR STATE
     * ========================================
     */

    /**
     * Set loading state
     */
    setLoading(loading) {
        this.setState({ loading });
    }

    /**
     * Set error state
     */
    setError(error) {
        this.setState({ error });

        if (error) {
            window.debugLogger.error('AppStore Error:', error);
            this.dispatchEvent('store:error', { error });
        }
    }

    /**
     * Clear error state
     */
    clearError() {
        this.setState({ error: null });
    }

    /**
     * ========================================
     * DEBUGGING & UTILITIES
     * ========================================
     */

    /**
     * Log to console (only in debug mode)
     */
    log(...args) {
        if (this.debug) {
            window.debugLogger.log('[AppStore]', ...args);
        }
    }

    /**
     * Dispatch custom event
     */
    dispatchEvent(eventName, detail = {}) {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            detail: {
                timestamp: Date.now(),
                ...detail
            }
        });

        document.dispatchEvent(event);
    }

    /**
     * Get debug info
     */
    getDebugInfo() {
        return {
            state: this.state,
            subscribers: this.subscribers.length,
            cartItemCount: this.getCartItemCount(),
            wishlistCount: this.getWishlistCount(),
            debug: this.debug
        };
    }

    /**
     * Enable debug mode
     */
    enableDebug() {
        this.debug = true;
        window.debugLogger.log('🐛 AppStore debug mode enabled');
        window.debugLogger.log(this.getDebugInfo());
    }

    /**
     * Disable debug mode
     */
    disableDebug() {
        this.debug = false;
    }
}

// Create global store instance
if (!window.appStore) {
    window.appStore = new AppStore();

    // Make store methods available for debugging in console
    if (window.appStore.debug) {
        window.debugLogger.log('🏪 AppStore is ready! Available methods:');
        window.debugLogger.log('- window.appStore.getState()');
        window.debugLogger.log('- window.appStore.getDebugInfo()');
        window.debugLogger.log('- window.appStore.subscribe(callback)');
    }
}

// Export for ES6 modules (if using build system)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = window.appStore;
}
